import { secureFetch } from "@/lib/queryClient";
import { Outlet } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Bell, User, LogOut, Settings, PanelRight, UserCircle } from "lucide-react";
import { Suspense, useEffect, useState } from "react";
import { SidebarProvider, SidebarTrigger, SidebarInset } from "@/components/ui/sidebar";
import { RouteLoadingFallback } from "@/components/layouts/RouteLoadingFallback";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { AppSidebar } from "@/components/AppSidebar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "next-themes";
import { logout } from "@/lib/authUtils";
import { useLocation } from "react-router-dom";
import { useNotifications } from "@/contexts/NotificationsContext";
import { useNotes } from "@/contexts/NotesContext";
import { useAuthContext } from "@/contexts/AuthContext";
import { LoginModal, type LoginModalTemplate } from "@/components/LoginModal";
import { shouldAttemptModalDecision, markModalShownThisSession, LOGIN_MODAL_SESSION_KEY } from "@/lib/loginModalGate";
import brainwrkLogoInk from "@/assets/brainwrk-logo-ink.png";
import brainwrkLogoSand from "@/assets/brainwrk-logo-sand.png";

const MODAL_DECISION_TIMEOUT_MS = 250;

const sidebarStyle = {
  "--sidebar-width": "16rem",
  "--sidebar-width-icon": "3rem",
} as React.CSSProperties;

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const { unreadCount } = useNotifications();
  const { isNotesOpen, toggleNotes } = useNotes();
  const { user, currentOrganization } = useAuthContext();
  const [loginModalTemplate, setLoginModalTemplate] = useState<LoginModalTemplate | null>(null);

  const fireAnalytics = (eventType: string, metadata?: Record<string, unknown>) => {
    secureFetch("/api/analytics/track", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ eventType, metadata }),
    }).catch(() => {});
  };

  useEffect(() => {
    if (!import.meta.env.VITE_ENABLE_LOGIN_MODAL) return;
    if (!user?.id) return;
    if (!shouldAttemptModalDecision(sessionStorage)) return;

    const initialPath = location.pathname;
    const orgId = currentOrganization?.id ?? null;

    fireAnalytics("login_modal_decision_requested", { orgId });

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), MODAL_DECISION_TIMEOUT_MS);

    secureFetch("/api/modal/decide-next", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orgId }),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data: { template?: LoginModalTemplate | null }) => {
        if (location.pathname !== initialPath) return;
        if (data?.template) {
          fireAnalytics("login_modal_rendered", {
            templateKey: data.template.key,
            templateId: data.template.id,
          });
          setLoginModalTemplate(data.template);
          markModalShownThisSession(sessionStorage);
        }
      })
      .catch(() => {})
      .finally(() => clearTimeout(timeout));
  }, [user?.id]);

  const handleSignOut = () => {
    logout();
  };

  return (
    <>
    <SidebarProvider style={sidebarStyle}>
      <div className="flex h-screen w-full" data-testid="app-shell-container">
        <AppSidebar />
        <SidebarInset className="flex flex-col overflow-hidden">
          <header className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-sm" data-testid="app-shell-header">
            <div className="flex items-center justify-between gap-2 h-14 px-4">
              <div className="flex items-center gap-3">
                <SidebarTrigger data-testid="button-sidebar-toggle" />
                <img
                  src={theme === 'dark' ? brainwrkLogoSand : brainwrkLogoInk}
                  alt="BrainWrk Logo"
                  className="h-6 w-auto"
                />
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={toggleNotes}
                  className="md:hidden text-muted-foreground"
                  aria-expanded={isNotesOpen}
                  data-testid="button-notes-panel-toggle"
                >
                  <PanelRight className="w-5 h-5" />
                </Button>

                <ThemeToggle />

                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full relative"
                  onClick={() => navigate("/notifications")}
                  data-testid="button-notifications"
                >
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                    >
                      {unreadCount}
                    </Badge>
                  )}
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild data-testid="button-user-menu">
                    <Button variant="ghost" size="icon" className="rounded-full">
                      <User className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-item-profile">
                      <UserCircle className="w-4 h-4 mr-2" />
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="menu-item-settings">
                      <Settings className="w-4 h-4 mr-2" />
                      Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleSignOut} data-testid="menu-item-sign-out">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-auto" data-testid="app-shell-main">
            <Suspense fallback={<RouteLoadingFallback />}>
              <AppErrorBoundary variant="shell">
                <Outlet />
              </AppErrorBoundary>
            </Suspense>
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
    {loginModalTemplate && (
      <LoginModal
        isOpen={true}
        template={loginModalTemplate}
        onImpression={() => fireAnalytics("login_modal_impression", { templateKey: loginModalTemplate.key, templateId: loginModalTemplate.id })}
        onPrimaryClick={() => fireAnalytics("login_modal_primary_clicked", { templateKey: loginModalTemplate.key })}
        onSecondaryClick={() => fireAnalytics("login_modal_secondary_clicked", { templateKey: loginModalTemplate.key })}
        onDismiss={() => {
          fireAnalytics("login_modal_dismissed", { templateKey: loginModalTemplate.key });
          setLoginModalTemplate(null);
        }}
      />
    )}
    </>
  );
}
